import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Zap, TrendingUp, ChevronRight, X } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";
import type { FeatureLimitData } from "./feature-limit-event";

const FEATURE_LABELS: Record<string, { label: string; icon: typeof Sparkles; color: string }> = {
  ai_copilot: { label: "Copilot", icon: Sparkles, color: "from-teal-500 to-cyan-500" },
  ai_agents: { label: "Agentes IA", icon: Zap, color: "from-violet-500 to-purple-500" },
  telemedicine: { label: "Telehealth", icon: TrendingUp, color: "from-blue-500 to-indigo-500" },
  coaching_sidebar: { label: "Coaching IA", icon: Sparkles, color: "from-amber-500 to-orange-500" },
  marketing_automation: { label: "Marketing", icon: TrendingUp, color: "from-pink-500 to-rose-500" },
};

export function FeatureLimitNotification() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<FeatureLimitData | null>(null);

  useEffect(() => {
    function handleEvent(e: CustomEvent<FeatureLimitData>) {
      setData(e.detail);
      setOpen(true);
    }
    window.addEventListener("feature-limit-reached", handleEvent as EventListener);
    return () => window.removeEventListener("feature-limit-reached", handleEvent as EventListener);
  }, []);

  if (!data) return null;

  const featureInfo = FEATURE_LABELS[data.feature] || {
    label: data.feature,
    icon: Sparkles,
    color: "from-teal-500 to-cyan-500",
  };
  const Icon = featureInfo.icon;
  const usagePercent = data.limit > 0 ? Math.min((data.used / data.limit) * 100, 100) : 100;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[28rem] p-0 overflow-hidden border-0">
        <DialogTitle className="sr-only">
          {`Limite atingido: ${featureInfo.label}`}
        </DialogTitle>
        <DialogDescription className="sr-only">{data.message}</DialogDescription>
        {/* Gradient header */}
        <div className={`bg-gradient-to-br ${featureInfo.color} px-6 pt-8 pb-12 relative`}>
          <button
            onClick={() => setOpen(false)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white/70 text-xs font-medium uppercase tracking-wider">Limite atingido</p>
              <h3 className="text-white text-xl font-bold">{featureInfo.label}</h3>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 -mt-6 relative z-10">
          <div className="bg-white rounded-xl border border-border shadow-lg p-5 space-y-4">
            {/* Usage bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Uso atual</span>
                <span className="font-semibold text-foreground">
                  {data.used}/{data.limit}
                </span>
              </div>
              <div className="w-full h-3 bg-zinc-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${featureInfo.color} transition-all duration-500`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
            </div>

            {/* Message */}
            <p className="text-sm text-muted-foreground leading-relaxed">
              {data.message}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 pt-4 space-y-3">
          <Button
            className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white py-3 h-12 text-base font-semibold shadow-lg shadow-teal-600/25"
            asChild
          >
            {/* SCRUM-208 (audit C-04): one place to choose a plan. */}
            <Link to="/onboarding/billing" onClick={() => setOpen(false)}>
              Escolher plano <ChevronRight className="w-5 h-5 ml-1" />
            </Link>
          </Button>
          <button
            onClick={() => setOpen(false)}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
          >
            Continuar com o plano atual
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
